import { TriangleAlert } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { CenteredMessage } from './CenteredMessage';
import { Button } from './ui/Button';

interface ErrorBoundaryProps {
  /** What couldn't be shown, e.g. "this board". */
  what: string;
  children: ReactNode;
}

/**
 * Catches errors thrown while drawing its children, so one broken part of
 * the app shows a message instead of leaving a blank page.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <CenteredMessage
        icon={<TriangleAlert className="size-6" />}
        title={`Something went wrong showing ${this.props.what}`}
      >
        <p>Your work is saved. Reloading usually sorts it out.</p>
        <p className="mt-4">
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </p>
      </CenteredMessage>
    );
  }
}
